import React, { useCallback, useEffect, useState } from 'react';
import {
    AlertTriangle,
    BarChart3,
    CheckCircle2,
    Coins,
    Download,
    Info,
    Loader2,
    PlusCircle,
    RefreshCw,
    Trash2,
    XCircle,
} from 'lucide-react';
import type { ResumoCustoIa, UsoIa } from '@nyroh/contracts';

import { Client } from '../types';
import { api, ErroApiNyroh } from '../lib/api';
import {
    clearCostLogs,
    fetchCostLogs,
    saveCostLog,
    type RegistroCusto,
} from '../lib/dados';

/**
 * ============================================================================
 * AUDITORIA DE CUSTOS — o que esta tela media, e o que ela mede agora
 * ============================================================================
 *
 * O QUE ELA MEDIA
 *
 * Nada. O número que ela somava vinha de três lugares e nenhum era medição:
 *
 *  1. `lib/gemini.ts` gravava `cost: 0.18` a cada geração — um valor lido do
 *     `localStorage`. O ramo que calcularia o custo por token estava logo acima,
 *     atrás de um `const usage = undefined` fixo: código morto por construção.
 *  2. Quando o histórico local estava vazio, esta tela INVENTAVA um: para cada
 *     rascunho com escopo, criava uma entrada de "falha" com
 *     `Math.random() > 0.7`. Cerca de 30% do histórico era ficção — e ficção que
 *     entrava na "Taxa de Sucesso (IA)".
 *  3. O card dizia "Novos sites usam custo real por tokens", que era falso desde
 *     o commit em que foi escrito.
 *
 * O QUE ELA MEDE AGORA
 *
 * `uso_ia`, gravada pela API a cada chamada ao modelo, com os tokens que o
 * próprio Gemini reporta. Chamada cujo modelo não tem preço cadastrado entra
 * como "sem preço" — nunca como zero, que somaria como se fosse de graça.
 *
 * O LANÇAMENTO MANUAL FICOU, e ficou SEPARADO
 *
 * Ele é legítimo: existe despesa de IA que não passa por esta API (uma conta
 * paga por fora, um crédito comprado). O que não pode é misturar com o medido e
 * apresentar a soma como se tudo tivesse sido apurado. As duas colunas são
 * mostradas lado a lado, com o total de cada uma.
 */

interface CostDashboardViewProps {
    clients: Client[];
}

type Periodo = 'hoje' | '7d' | '30d' | 'todos';

const ROTULO: Record<Periodo, string> = {
    hoje: 'Hoje',
    '7d': 'Últimos 7d',
    '30d': 'Últimos 30d',
    todos: 'Tudo',
};

/** Janela em milissegundos, para filtrar os lançamentos manuais do mesmo jeito. */
const JANELA_MS: Record<Periodo, number | null> = {
    hoje: 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000,
    todos: null,
};

const reais = (v: number) =>
    `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;

const numero = (v: number) => v.toLocaleString('pt-BR');

function mensagemDeErro(e: unknown): string {
    if (e instanceof ErroApiNyroh) return e.message;
    return e instanceof Error ? e.message : String(e);
}

export const CostDashboardView: React.FC<CostDashboardViewProps> = ({ clients }) => {
    const [periodo, setPeriodo] = useState<Periodo>('7d');
    const [resumo, setResumo] = useState<ResumoCustoIa | null>(null);
    const [chamadas, setChamadas] = useState<UsoIa[]>([]);
    const [manuais, setManuais] = useState<RegistroCusto[]>([]);

    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);

    const [mostrarFormulario, setMostrarFormulario] = useState(false);
    const [nomeManual, setNomeManual] = useState('');
    const [valorManual, setValorManual] = useState('');
    const [statusManual, setStatusManual] = useState<'success' | 'failed'>('success');

    const carregar = useCallback(async (p: Periodo) => {
        setCarregando(true);
        setErro(null);
        try {
            const [r, u, m] = await Promise.all([
                api.chamar('ia.resumoCusto', { query: { periodo: p } }),
                api.chamar('ia.uso', { query: { periodo: p, limite: 100 } }),
                fetchCostLogs(),
            ]);
            setResumo(r);
            setChamadas(u.itens);
            setManuais(m ?? []);
        } catch (e) {
            setErro(mensagemDeErro(e));
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => {
        void carregar(periodo);
    }, [carregar, periodo]);

    const janela = JANELA_MS[periodo];
    const manuaisNoPeriodo = manuais.filter(
        (m) => janela === null || Date.now() - m.timestamp <= janela,
    );
    const totalManual = manuaisNoPeriodo.reduce((s, m) => s + m.cost, 0);

    const lancarManual = async (e: React.FormEvent) => {
        e.preventDefault();
        const nome = nomeManual.trim();
        const valor = parseFloat(valorManual);
        if (!nome || !Number.isFinite(valor)) return;

        /*
          O id vem de `crypto.randomUUID`, não de `Math.random`.

          O anterior era `'manual-' + Math.random().toString(36).substring(7)` —
          cerca de 5 caracteres de base36. Colisão nesse espaço não é hipótese
          remota, e o id é a chave do registro no banco: colidir sobrescreve o
          lançamento de outra pessoa em silêncio.
        */
        const entrada: RegistroCusto = {
            id: `manual-${crypto.randomUUID()}`,
            timestamp: Date.now(),
            clientName: nome,
            status: statusManual,
            cost: valor,
        };

        setManuais((atual) => [entrada, ...atual]);
        const ok = await saveCostLog(entrada);
        if (!ok) {
            setErro('Não consegui gravar o lançamento no servidor. Ele não foi salvo.');
            setManuais((atual) => atual.filter((m) => m.id !== entrada.id));
            return;
        }

        setNomeManual('');
        setValorManual('');
        setStatusManual('success');
        setMostrarFormulario(false);
    };

    const limparManuais = async () => {
        if (
            !window.confirm(
                'Isto apaga apenas os LANÇAMENTOS MANUAIS. O gasto medido de IA fica: ' +
                    'ele é o registro do que já foi cobrado pelo provedor e não é editável por aqui.',
            )
        ) {
            return;
        }
        const ok = await clearCostLogs();
        if (ok) setManuais([]);
        else setErro('Não consegui limpar os lançamentos manuais.');
    };

    const exportarCsv = () => {
        if (chamadas.length === 0 && manuaisNoPeriodo.length === 0) {
            alert('Nenhum dado para exportar no filtro atual.');
            return;
        }

        const cabecalho = [
            'Origem',
            'Data/Hora',
            'Cliente',
            'Modelo',
            'Tokens',
            'Status',
            'Custo (R$)',
        ];
        const linhas = [
            ...chamadas.map((c) => [
                'medido',
                new Date(c.criadoEm).toLocaleString('pt-BR'),
                `"${(c.clienteNome ?? '—').replace(/"/g, '""')}"`,
                c.modelo,
                String(c.tokensTotal),
                c.sucesso ? 'Sucesso' : 'Falha',
                // Sem preço cadastrado sai VAZIO, não zero: zero num CSV é lido
                // como "custou nada", e o que houve foi "não sei quanto custou".
                c.custoBrl === null ? '' : c.custoBrl.toFixed(4).replace('.', ','),
            ]),
            ...manuaisNoPeriodo.map((m) => [
                'manual',
                new Date(m.timestamp).toLocaleString('pt-BR'),
                `"${m.clientName.replace(/"/g, '""')}"`,
                '',
                '',
                m.status === 'success' ? 'Sucesso' : 'Falha',
                m.cost.toFixed(4).replace('.', ','),
            ]),
        ];

        const csv =
            'data:text/csv;charset=utf-8,﻿' +
            [cabecalho.join(';'), ...linhas.map((r) => r.join(';'))].join('\n');
        const link = document.createElement('a');
        link.setAttribute('href', encodeURI(csv));
        link.setAttribute('download', `custos-ia-${periodo}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const maxDia = Math.max(...(resumo?.porDia.map((d) => d.custoBrl) ?? [0]), 0.0001);

    return (
        <div className="p-8 max-w-6xl mx-auto space-y-8 animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-200 pb-6">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <Coins className="text-orange-600 w-7 h-7" />
                        Auditoria de Custos da Inteligência
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Gasto <strong>medido</strong> em tokens reais, direto de <code>uso_ia</code>. Para escolher
                        o modelo e ver o custo por site, use a aba <strong>Inteligência Artificial</strong>.
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <div className="bg-white border border-zinc-200 shadow-sm rounded-sm p-1 flex gap-1">
                        {(Object.keys(ROTULO) as Periodo[]).map((p) => (
                            <button
                                key={p}
                                onClick={() => setPeriodo(p)}
                                className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-tight transition-all ${
                                    periodo === p
                                        ? 'bg-slate-900 text-white'
                                        : 'text-slate-500 hover:text-slate-800 hover:bg-zinc-50'
                                }`}
                            >
                                {ROTULO[p]}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => void carregar(periodo)}
                        className="bg-white hover:bg-zinc-50 border border-zinc-200 text-slate-800 px-3 py-2 rounded-sm text-xs font-semibold flex items-center gap-1.5 shadow-sm"
                    >
                        <RefreshCw size={14} className={carregando ? 'animate-spin' : ''} /> Atualizar
                    </button>
                    <button
                        onClick={exportarCsv}
                        className="bg-white hover:bg-zinc-50 border border-zinc-200 text-slate-800 px-3 py-2 rounded-sm text-xs font-semibold flex items-center gap-1.5 shadow-sm"
                    >
                        <Download size={14} /> Exportar CSV
                    </button>
                </div>
            </div>

            {erro && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-sm p-4 text-sm flex items-start gap-2">
                    <XCircle size={16} className="mt-0.5 shrink-0" />
                    <span>{erro}</span>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Cartao
                    rotulo="Gasto medido (IA)"
                    valor={resumo ? reais(resumo.custoBrl) : '—'}
                    nota={resumo ? `US$ ${resumo.custoUsd.toFixed(4)} no provedor` : ''}
                    destaque
                />
                <Cartao
                    rotulo="Custo médio por site"
                    valor={
                        resumo?.custoMedioPorSiteBrl === null || resumo === null
                            ? '—'
                            : reais(resumo.custoMedioPorSiteBrl)
                    }
                    nota={resumo ? `${numero(resumo.sitesAtendidos)} site(s) no período` : ''}
                />
                <Cartao
                    rotulo="Chamadas ao modelo"
                    valor={resumo ? numero(resumo.chamadas) : '—'}
                    nota={resumo ? `${numero(resumo.falhas)} falha(s)` : ''}
                />
                <Cartao
                    rotulo="Lançado à mão"
                    valor={reais(totalManual)}
                    nota={`${numero(manuaisNoPeriodo.length)} lançamento(s) — não é medição`}
                />
            </div>

            {resumo && resumo.chamadasSemPreco > 0 && (
                <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-sm p-4 text-sm flex items-start gap-2">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>
                        <strong>{numero(resumo.chamadasSemPreco)} chamada(s)</strong> usaram um modelo sem preço
                        cadastrado e <strong>não entram</strong> no "Gasto medido". O gasto delas é real; falta o
                        preço para calculá-lo. Cadastre em <strong>Inteligência Artificial → Modelo</strong>.
                    </span>
                </div>
            )}

            {/* ------------------------------------------------- curva diária -- */}
            <div className="bg-white border border-zinc-200 rounded-sm p-6 shadow-sm">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest mb-6">
                    <BarChart3 size={14} /> Curva diária de investimento
                </div>
                {!resumo || resumo.porDia.length === 0 ? (
                    <div className="text-sm text-slate-400 py-8 text-center">
                        Nenhuma chamada de IA registrada no período.
                    </div>
                ) : (
                    <div className="flex items-end gap-1.5 h-40">
                        {resumo.porDia.map((d) => (
                            <div key={d.dia} className="flex-1 flex flex-col items-center gap-1 group">
                                <div className="text-[10px] font-mono text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {reais(d.custoBrl)}
                                </div>
                                <div
                                    className="w-full bg-orange-600/80 hover:bg-orange-600 rounded-t-sm transition-all min-h-[2px]"
                                    style={{ height: `${(d.custoBrl / maxDia) * 100}%` }}
                                    title={`${d.dia}: ${reais(d.custoBrl)} em ${d.chamadas} chamada(s)`}
                                />
                                <div className="text-[10px] text-slate-400 font-mono">
                                    {d.dia.slice(8)}/{d.dia.slice(5, 7)}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* -------------------------------------------- lançamento manual -- */}
            <div className="bg-white border border-zinc-200 rounded-sm p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                            Lançamentos manuais
                        </div>
                        <p className="text-xs text-slate-500 mt-1 flex items-start gap-1.5">
                            <Info size={12} className="mt-0.5 shrink-0" />
                            Para despesa de IA que <strong>não passou por esta API</strong> (crédito comprado,
                            conta paga por fora). Fica separado do medido de propósito: somar os dois e apresentar
                            como apuração seria o erro que esta tela cometia.
                        </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <button
                            onClick={() => setMostrarFormulario((v) => !v)}
                            className="bg-white border border-zinc-200 hover:bg-zinc-50 text-slate-800 px-3 py-2 rounded-sm text-xs font-semibold flex items-center gap-1.5"
                        >
                            <PlusCircle size={14} /> Lançar
                        </button>
                        {manuais.length > 0 && (
                            <button
                                onClick={limparManuais}
                                className="bg-white border border-zinc-200 hover:bg-red-50 hover:border-red-200 text-red-700 px-3 py-2 rounded-sm text-xs font-semibold flex items-center gap-1.5"
                            >
                                <Trash2 size={14} /> Limpar
                            </button>
                        )}
                    </div>
                </div>

                {mostrarFormulario && (
                    <form
                        onSubmit={lancarManual}
                        className="grid grid-cols-1 md:grid-cols-4 gap-3 border-t border-zinc-100 pt-4"
                    >
                        <input
                            value={nomeManual}
                            onChange={(e) => setNomeManual(e.target.value)}
                            placeholder="Descrição / projeto"
                            className="md:col-span-2 bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2 text-sm"
                        />
                        <input
                            value={valorManual}
                            onChange={(e) => setValorManual(e.target.value)}
                            type="number"
                            step="0.0001"
                            min="0"
                            placeholder="Valor em R$"
                            className="bg-zinc-50 border border-zinc-200 rounded-sm px-3 py-2 text-sm font-mono"
                        />
                        <div className="flex gap-2">
                            <select
                                value={statusManual}
                                onChange={(e) => setStatusManual(e.target.value as 'success' | 'failed')}
                                className="flex-1 bg-zinc-50 border border-zinc-200 rounded-sm px-2 py-2 text-sm"
                            >
                                <option value="success">Sucesso</option>
                                <option value="failed">Falha</option>
                            </select>
                            <button
                                type="submit"
                                className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-sm text-sm font-bold"
                            >
                                OK
                            </button>
                        </div>
                    </form>
                )}

                {manuaisNoPeriodo.length > 0 && (
                    <div className="border-t border-zinc-100 pt-4 space-y-1">
                        {manuaisNoPeriodo.slice(0, 20).map((m) => (
                            <div
                                key={m.id}
                                className="flex items-center justify-between text-sm py-1.5 border-b border-zinc-50 last:border-0"
                            >
                                <span className="text-slate-700">{m.clientName}</span>
                                <span className="text-xs text-slate-400">
                                    {new Date(m.timestamp).toLocaleString('pt-BR')}
                                </span>
                                <span className="font-semibold text-slate-900">{reais(m.cost)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ----------------------------------------------------- histórico -- */}
            <div className="bg-white border border-zinc-200 rounded-sm shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-zinc-100 text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center justify-between">
                    <span>Chamadas de IA — medidas</span>
                    {carregando && <Loader2 size={14} className="animate-spin" />}
                </div>
                {chamadas.length === 0 ? (
                    <div className="px-6 py-10 text-center text-sm text-slate-400">
                        Nenhuma chamada de IA no período. {clients.length > 0 && 'Gere um site para começar a medir.'}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs font-bold text-slate-400 uppercase tracking-widest bg-zinc-50">
                                    <th className="px-6 py-3">Data</th>
                                    <th className="px-4 py-3">Cliente</th>
                                    <th className="px-4 py-3">Modelo</th>
                                    <th className="px-4 py-3">Origem</th>
                                    <th className="px-4 py-3 text-right">Tokens</th>
                                    <th className="px-4 py-3 text-center">Status</th>
                                    <th className="px-6 py-3 text-right">Custo</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100">
                                {chamadas.map((c) => (
                                    <tr key={c.id}>
                                        <td className="px-6 py-3 text-xs text-slate-500 whitespace-nowrap">
                                            {new Date(c.criadoEm).toLocaleString('pt-BR')}
                                        </td>
                                        <td className="px-4 py-3 text-slate-800">{c.clienteNome ?? '—'}</td>
                                        <td className="px-4 py-3 font-mono text-xs text-slate-600">{c.modelo}</td>
                                        <td className="px-4 py-3 text-xs text-slate-500">{c.origem}</td>
                                        <td className="px-4 py-3 text-right font-mono text-xs">
                                            {numero(c.tokensTotal)}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            {c.sucesso ? (
                                                <CheckCircle2 size={14} className="text-emerald-600 inline" />
                                            ) : (
                                                <XCircle size={14} className="text-red-600 inline" />
                                            )}
                                        </td>
                                        <td className="px-6 py-3 text-right font-semibold">
                                            {c.custoBrl === null ? (
                                                <span
                                                    className="text-amber-700 text-xs"
                                                    title="Modelo sem preço cadastrado. O gasto é real; falta o preço para calculá-lo."
                                                >
                                                    sem preço
                                                </span>
                                            ) : (
                                                reais(c.custoBrl)
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

const Cartao: React.FC<{ rotulo: string; valor: string; nota: string; destaque?: boolean }> = ({
    rotulo,
    valor,
    nota,
    destaque,
}) => (
    <div
        className={`border rounded-sm p-6 shadow-sm ${
            destaque ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-zinc-200'
        }`}
    >
        <div
            className={`text-xs font-bold uppercase tracking-widest ${
                destaque ? 'text-zinc-400' : 'text-slate-400'
            }`}
        >
            {rotulo}
        </div>
        <div className="text-2xl font-bold mt-2 tabular-nums">{valor}</div>
        <div className={`text-xs mt-1 ${destaque ? 'text-zinc-400' : 'text-slate-400'}`}>{nota}</div>
    </div>
);
